/**
 * useTriggerSchemaSample Hook
 *
 * Fetches a pre-generated trigger output sample from Otto's `schemaSamples`
 * catalog. Returns `data: null` when no sample exists or auth/network fails —
 * callers fall back to the heuristic sample generator in `testDataService`.
 *
 * The hook is intentionally lenient: missing samples are expected, and the
 * portal must never block the Test Draft dialog on a 404. We cache for 24h
 * because catalog entries are immutable for the lifetime of a runtime image.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { fetchTriggerSchemaSample, pickSchemaSource } from '@/services/schemaSamplesService';
import { extractConnectorName } from '@/utils/connections';

const QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 60 * 24, // 24h — samples are immutable per runtime image
  gcTime: 1000 * 60 * 60 * 24,
  refetchOnMount: false as const,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false as const,
  retry: 1,
};

export interface UseTriggerSchemaSampleArgs {
  /** Full connector ID (e.g. `/automationProjects/p/managedApis/office365`). */
  connectorId: string | null | undefined;
  /** The operation ID resolved from the trigger definition (e.g. `OnNewEmail`). */
  operationId: string | null | undefined;
  /** The trigger type string from the workflow (e.g. `ApiConnectionWebhook`). */
  triggerType: string | null | undefined;
}

export const useTriggerSchemaSample = ({
  connectorId,
  operationId,
  triggerType,
}: UseTriggerSchemaSampleArgs) => {
  const { getOttoToken } = useAuth();
  const { selectedApp } = useAppStore();

  const projectName = selectedApp?.projectName ?? null;
  const apiName = connectorId ? extractConnectorName(connectorId) : null;
  const schemaSource = pickSchemaSource(triggerType);

  const isEnabled = !!projectName && !!apiName && !!operationId;

  return useQuery<Record<string, unknown> | null>({
    queryKey: ['triggerSchemaSample', projectName, apiName, operationId, schemaSource],
    queryFn: async () => {
      const token = await getOttoToken();
      // Token may be absent in local/mock modes; the service handles that path.
      return fetchTriggerSchemaSample({
        projectName: projectName ?? '',
        apiName: apiName ?? '',
        operationName: operationId ?? '',
        schemaSource,
        accessToken: token ?? '',
      });
    },
    enabled: isEnabled,
    ...QUERY_OPTIONS,
  });
};
